import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Calendar, Clock, ArrowRight } from "lucide-react";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}

export default function Blog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/public/blog/articles");
        if (!cancelled) {
          setItems(r.data.items || []);
          setError(r.data.error || null);
        }
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Could not load articles");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <main data-testid="blog-page" className="px-6 md:px-12 lg:px-16 pt-16 pb-24">
      <div className="label-eyebrow mb-6">Insights &amp; Guides</div>
      <h1 className="font-black tracking-tighter leading-[0.95] text-5xl md:text-7xl max-w-3xl">Insights &amp; Guides.</h1>
      <p className="mt-6 text-[#6B7280] max-w-2xl text-lg">Practical advice on planning, permitted development, pricing and what to expect when you extend your home. Written by the team who builds it.</p>

      {loading && (
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-loading">
          {[0, 1, 2].map(i => (
            <div key={i} className="de-card p-6 animate-pulse">
              <div className="aspect-[16/9] bg-[#F0EAE0] mb-4"/>
              <div className="h-5 bg-[#F0EAE0] mb-3 w-3/4"/>
              <div className="h-3 bg-[#F0EAE0] mb-2 w-full"/>
              <div className="h-3 bg-[#F0EAE0] w-2/3"/>
            </div>
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="mt-16 de-card p-12 max-w-2xl" data-testid="blog-empty">
          <div className="label-eyebrow mb-3 text-[#C79A3B]">Coming soon</div>
          <p className="text-lg mb-4">Our first articles are on their way — expect straight-talking guides on planning, extension design and getting your project on-site without the stress.</p>
          <p className="text-sm text-[#6B7280]">In the meantime, our <Link to="/extension-rules" className="de-link font-semibold">Extension Rules</Link> page walks you through Permitted Development limits, and <Link to="/faq" className="de-link font-semibold">FAQs</Link> covers the questions we hear most.</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="blog-grid">
          {items.map(a => <ArticleCard key={a.id} article={a}/>)}
        </div>
      )}

      {error && !loading && (
        <p className="mt-8 text-xs text-[#6B7280]" data-testid="blog-error">Some articles may be unavailable right now &mdash; we&apos;ll refresh again shortly.</p>
      )}
    </main>
  );
}

function ArticleCard({ article }) {
  const href = `/blog/${article.id}`;
  return (
    <Link to={href} data-testid={`blog-card-${article.id}`}
      className="group de-card overflow-hidden hover:border-[#C79A3B] transition-colors flex flex-col">
      {article.cover_image
        ? <div className="aspect-[16/9] overflow-hidden bg-[#F0EAE0]"><img src={article.cover_image} alt={article.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"/></div>
        : <div className="aspect-[16/9] bg-[#12263A] flex items-center justify-center"><span className="text-[10px] uppercase tracking-[3px] text-[#C79A3B] font-bold">The Extension Works</span></div>}
      <div className="p-6 flex flex-col flex-1">
        {(article.tags && article.tags.length > 0) && (
          <div className="label-eyebrow text-[#C79A3B] mb-2 truncate">{(article.tags[0] || "").toString()}</div>
        )}
        <h2 className="text-xl font-bold tracking-tight mb-2 leading-snug line-clamp-3">{article.title}</h2>
        {article.excerpt && <p className="text-sm text-[#6B7280] mb-4 line-clamp-3">{article.excerpt}</p>}
        <div className="mt-auto pt-3 border-t border-[#F0EAE0] flex items-center justify-between text-xs text-[#6B7280]">
          <span className="inline-flex items-center gap-1.5">
            {article.published_at && <><Calendar size={12}/> {formatDate(article.published_at)}</>}
          </span>
          {article.reading_time_min && <span className="inline-flex items-center gap-1"><Clock size={12}/> {article.reading_time_min} min</span>}
        </div>
        <span className="mt-4 inline-flex items-center gap-1 text-[#C79A3B] font-bold text-sm group-hover:gap-2 transition-all">Read article <ArrowRight size={14}/></span>
      </div>
    </Link>
  );
}
